'use server';

import { UnifiedAIConfigService } from '@/contexts/ai/services/config/UnifiedAIConfigService';
import type { OperationType } from '@/contexts/ai/types/config';

/**
 * One prospective operation to price for the user-facing "~N tokens" preview.
 * `textLength` (chars) and `imageCount` are the EXPECTED SIZE — not price knowledge;
 * accounts turns them into the real charge.
 */
export interface EstimateLine {
  opType: OperationType;
  /** Expected characters (TTS ops) — priced per 1k_chars by accounts. */
  textLength?: number;
  /** Expected image count (imagen ops). */
  imageCount?: number;
}

/**
 * Price prospective operations against ACCOUNTS so the preview equals what accounts
 * will actually charge (issue #79 — the preview was the last portal-local sell-rate).
 *
 * Each line resolves through `UnifiedAIConfigService.estimateOperationReservation`,
 * which builds `{op_type, provider, model, units}` from the ACTIVE model-selection
 * config + the expected size and asks accounts `POST /billing/estimate` (S2S,
 * side-effect-free) for `credits`. When accounts can't price the key (unpriced /
 * network / no pre-op unit estimate, e.g. LLM) it falls back to the flat per-op-type
 * hold from the operations registry — so the preview never blanks or blocks the UX.
 *
 * Public pricing data only (no user-specific info) → intentionally session-auth-free
 * (see actionAuthGuard allowlist), same as getTokenPricing.
 */
export async function estimateOperationCosts(lines: EstimateLine[]): Promise<number[]> {
  return Promise.all(
    lines.map((line) =>
      UnifiedAIConfigService.estimateOperationReservation(line.opType, {
        textLength: line.textLength,
        imageCount: line.imageCount,
      }),
    ),
  );
}
